import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { PageHeader } from '../components/PageHeader';
import { Plus, Edit2, Trash2, KeyRound } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { ROLE_LABELS, formatDate } from '../lib/format';
import { useToast } from '../hooks/use-toast';

const initialForm = { username: '', full_name: '', email: '', role: 'requestor', company_name: '', department: '', is_active: true, password: '' };

const Users = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const [resetOpen, setResetOpen] = useState(null);
  const [newPw, setNewPw] = useState('');

  const isSuperAdmin = hasRole(user, 'super_admin');

  const load = useCallback(async () => {
    setUsers((await api.get('/users')).data);
    try { setRoles((await api.get('/roles')).data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const roleLabel = (key) => roles.find((r) => r.key === key)?.label || ROLE_LABELS[key] || key;

  const save = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
        toast({ title: 'Updated' });
      } else {
        await api.post('/users', form);
        toast({ title: 'User created' });
      }
      setOpen(false); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };
  const remove = async (u) => { if (!window.confirm(`Delete ${u.username}?`)) return; try { await api.delete(`/users/${u.id}`); load(); } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); } };
  const resetPassword = async () => {
    if (!newPw || newPw.length < 4) { toast({ title: 'Password too short', variant: 'destructive' }); return; }
    await api.post(`/users/${resetOpen.id}/reset-password`, { password: newPw });
    toast({ title: 'Password reset' });
    setResetOpen(null); setNewPw('');
  };

  return (
    <div className="space-y-4">
      <PageHeader title="User Management" description="Manage user accounts, roles, and permissions."
        actions={<Button onClick={() => { setEditing(null); setForm(initialForm); setOpen(true); }} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"><Plus className="h-4 w-4 mr-1.5" />Add User</Button>} />
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-left text-slate-600"><tr><th className="p-3">User</th><th className="p-3">Role</th><th className="p-3">Company / Department</th><th className="p-3">Status</th><th className="p-3">Created</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody className="divide-y">
            {users.map((u) => (
              <tr key={u.id} className="table-row-hover">
                <td className="p-3"><div className="flex items-center gap-2"><div className="h-8 w-8 rounded-full bg-[hsl(217,91%,24%)] text-white grid place-items-center text-xs font-bold">{u.full_name.split(' ').map(s=>s[0]).slice(0,2).join('')}</div><div><div className="font-medium">{u.full_name}</div><div className="text-xs text-slate-500 font-mono">{u.username} • {u.email}</div></div></div></td>
                <td className="p-3"><Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100">{roleLabel(u.role)}</Badge></td>
                <td className="p-3 text-xs">{u.company_name}<div className="text-slate-500">{u.department}</div></td>
                <td className="p-3">{u.is_active ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</td>
                <td className="p-3 text-xs">{formatDate(u.created_at)}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(u); setForm({ ...initialForm, ...u, password: '' }); setOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
                  {isSuperAdmin && <Button size="sm" variant="ghost" onClick={() => setResetOpen(u)} title="Reset password"><KeyRound className="h-4 w-4" /></Button>}
                  {isSuperAdmin && u.id !== user.id && <Button size="sm" variant="ghost" onClick={() => remove(u)}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit User' : 'Add User'}</DialogTitle><DialogDescription>{editing ? 'Leave password empty to keep current.' : 'New user account details.'}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Username *</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} disabled={!!editing} /></div>
            <div><Label>Full Name *</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}{!r.is_builtin && <span className="ml-1 text-[10px] text-slate-400">(custom)</span>}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Company</Label><Input value={form.company_name || ''} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></div>
            <div><Label>Department</Label><Input value={form.department || ''} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
            <div><Label>Password {editing && <span className="text-xs text-slate-400">(leave empty to keep)</span>}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="flex items-end"><label className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} /> <span className="text-sm">Active</span></label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">{editing ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetOpen} onOpenChange={(o) => !o && setResetOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password</DialogTitle><DialogDescription>Setting new password for <strong>{resetOpen?.username}</strong>.</DialogDescription></DialogHeader>
          <Label>New Password</Label>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
          <DialogFooter><Button variant="outline" onClick={() => setResetOpen(null)}>Cancel</Button><Button onClick={resetPassword} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
